import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { LaunchEntitlementsSync } from '@/components/launch/LaunchEntitlementsSync'
import { LaunchGate } from '@/components/launch/LaunchGate'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { AuthProvider } from '@/components/auth/AuthProvider'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { ProtectedAdminRoute } from '@/components/auth/ProtectedAdminRoute'
import { AppLayout } from '@/components/layout/AppLayout'
import { ToastProvider } from '@/components/ui/toast'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { PageErrorBoundary } from '@/components/shared/PageErrorBoundary'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { PageSkeleton } from '@/components/shared/PageSkeleton'

const LandingPage = lazy(() => import('@/pages/LandingPage'))
const PricingPage = lazy(() => import('@/pages/PricingPage'))
const TermsPage = lazy(() => import('@/pages/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'))
const DocsPage = lazy(() => import('@/pages/DocsPage'))
const AboutPage = lazy(() => import('@/pages/AboutPage'))
const BlogPage = lazy(() => import('@/pages/BlogPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage'))
const BuildPage = lazy(() => import('@/pages/BuildPage'))
const TemplateGalleryPage = lazy(() => import('@/pages/TemplateGalleryPage'))
const TemplatePreviewPage = lazy(() => import('@/pages/TemplatePreviewPage'))
const TemplateOpenPage = lazy(() => import('@/pages/TemplateOpenPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const TokenUsagePage = lazy(() => import('@/pages/TokenUsagePage'))
const AuditLogPage = lazy(() => import('@/pages/AuditLogPage'))
const KnowledgePage = lazy(() => import('@/pages/KnowledgePage'))
const MarketingDashboardPage = lazy(() => import('@/pages/MarketingDashboardPage'))
const CampaignManagerPage = lazy(() => import('@/pages/CampaignManagerPage'))
const ContentCalendarPage = lazy(() => import('@/pages/ContentCalendarPage'))
const LeadPipelinePage = lazy(() => import('@/pages/LeadPipelinePage'))
const BillingPage = lazy(() => import('@/pages/BillingPage'))
const AgencyOverviewPage = lazy(() => import('@/pages/AgencyOverviewPage'))
const TeamPage = lazy(() => import('@/pages/TeamPage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const ProjectSettingsPage = lazy(() => import('@/pages/ProjectSettingsPage'))
const ConnectorSetupPage = lazy(() => import('@/pages/ConnectorSetupPage'))
const HelpPage = lazy(() => import('@/pages/HelpPage'))
const CampaignAnalyticsPage = lazy(() => import('@/pages/CampaignAnalyticsPage'))
const NurtureEditorPage = lazy(() => import('@/pages/NurtureEditorPage'))
const MarketingPersonasPage = lazy(() => import('@/pages/MarketingPersonasPage'))
const MarketingStrategyPage = lazy(() => import('@/pages/MarketingStrategyPage'))
const ABTestPage = lazy(() => import('@/pages/ABTestPage'))
const RulesPage = lazy(() => import('@/pages/RulesPage'))
const ModelSettingsPage = lazy(() => import('@/pages/ModelSettingsPage'))
const AdminProvidersPage = lazy(() => import('@/pages/AdminProvidersPage'))
const AdminCatalogPage = lazy(() => import('@/pages/AdminCatalogPage'))
const AdminAgentsPage = lazy(() => import('@/pages/AdminAgentsPage'))
const AdminOpsPage = lazy(() => import('@/pages/AdminOpsPage'))
const AdminSystemPage = lazy(() => import('@/pages/AdminSystemPage'))
const AdminUserSupportPage = lazy(() => import('@/pages/admin/UserSupport'))
const AdminPromotionQueuePage = lazy(() => import('@/pages/admin/PromotionQueue'))
const DeploymentPage = lazy(() => import('@/pages/DeploymentPage'))
const TracesPage = lazy(() => import('@/pages/TracesPage'))
const MonitoringPage = lazy(() => import('@/pages/MonitoringPage'))
const BrainPage = lazy(() => import('@/pages/BrainPage'))
const ApprovalQueuePage = lazy(() => import('@/pages/ApprovalQueuePage'))
const CostForecastPage = lazy(() => import('@/pages/CostForecastPage'))
const AppPreviewsPage = lazy(() => import('@/pages/AppPreviewsPage'))
const AppLogsPage = lazy(() => import('@/pages/AppLogsPage'))
const AppMetricsPage = lazy(() => import('@/pages/AppMetricsPage'))
const SchedulerPage = lazy(() => import('@/pages/SchedulerPage'))
const GeographyConfigPage = lazy(() => import('@/pages/GeographyConfigPage'))
const AssetLibraryPage = lazy(() => import('@/pages/AssetLibraryPage'))
const TaskMonitorPage = lazy(() => import('@/pages/TaskMonitorPage'))
const ConversionTrackingPage = lazy(() => import('@/pages/ConversionTrackingPage'))
const SupplyDemandPage = lazy(() => import('@/pages/SupplyDemandPage'))
const SeoManagerPage = lazy(() => import('@/pages/SeoManagerPage'))
const WebhookConfigPage = lazy(() => import('@/pages/WebhookConfigPage'))
const ReportsPage = lazy(() => import('@/pages/ReportsPage'))
const TenantConfigPage = lazy(() => import('@/pages/TenantConfigPage'))
const ReferralProgramPage = lazy(() => import('@/pages/ReferralProgramPage'))
const ProviderDiscoveryPage = lazy(() => import('@/pages/ProviderDiscoveryPage'))
const ContentStudioPage = lazy(() => import('@/pages/ContentStudioPage'))
const SessionDetailPage = lazy(() => import('@/pages/SessionDetailPage'))
const CalendarPage = lazy(() => import('@/pages/CalendarPage'))
const AdCreativesPage = lazy(() => import('@/pages/AdCreativesPage'))
const ContentAnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'))
const LanguageVoicePage = lazy(() => import('@/pages/LanguageVoicePage'))
const TemplateMarketplacePage = lazy(() => import('@/pages/TemplateMarketplacePage'))
const CompetitiveIntelPage = lazy(() => import('@/pages/CompetitiveIntelPage'))
const CmsIntegrationsPage = lazy(() => import('@/pages/CmsIntegrationsPage'))
const BulkGenerationPage = lazy(() => import('@/pages/BulkGenerationPage'))
const AvatarStudioPage = lazy(() => import('@/pages/AvatarStudioPage'))
const VideoGenerationPage = lazy(() => import('@/pages/VideoGenerationPage'))
const MediaStudioPage = lazy(() => import('@/pages/MediaStudioPage'))
const BlogManagerPage = lazy(() => import('@/pages/BlogManagerPage'))
// BuildAnalyticsPage deleted in Plan 30 A4 — v1 analytics module + endpoints gone.
const DeveloperPortalPage = lazy(() => import('@/pages/DeveloperPortalPage'))
const AuthorEarningsPage = lazy(() => import('@/pages/AuthorEarningsPage'))
const ComplianceDashboardPage = lazy(() => import('@/pages/ComplianceDashboardPage'))
const RbacSettingsPage = lazy(() => import('@/pages/RbacSettingsPage'))
const SsoSettingsPage = lazy(() => import('@/pages/SsoSettingsPage'))
const SlaDashboardPage = lazy(() => import('@/pages/SlaDashboardPage'))
// Phase 9: AI Agent Intelligence
const KnowledgeGraphPage = lazy(() => import('@/pages/KnowledgeGraphPage'))
const ContextBudgetPage = lazy(() => import('@/pages/ContextBudgetPage'))
// AgentCoordinationPage + ErrorPredictionPage deleted in Plan 30 A4 — v1 multi-agent introspection gone.
const SelfHealingPage = lazy(() => import('@/pages/SelfHealingPage'))
const CorrectionLearningPage = lazy(() => import('@/pages/CorrectionLearningPage'))
const AutonomousDevPage = lazy(() => import('@/pages/AutonomousDevPage'))
// Plan 14: Company-in-a-Box
const RetentionDashboardPage = lazy(() => import('@/pages/RetentionDashboardPage'))
const AgencyDashboardPage = lazy(() => import('@/pages/AgencyDashboardPage'))
const GrowthSetupPage = lazy(() => import('@/pages/GrowthSetupPage'))
// Phase 10: Growth & Distribution
const ShareablePreviewPage = lazy(() => import('@/pages/ShareablePreviewPage'))
const ReferralEnhancedPage = lazy(() => import('@/pages/ReferralEnhancedPage'))
const ShowcasePage = lazy(() => import('@/pages/ShowcasePage'))
const WidgetConfigPage = lazy(() => import('@/pages/WidgetConfigPage'))
const UserAppMarketingPage = lazy(() => import('@/pages/UserAppMarketingPage'))
const OnboardingSetupPage = lazy(() => import('@/pages/OnboardingSetupPage'))
const CostMeteringPage = lazy(() => import('@/pages/CostMeteringPage'))
const MarketplacePage = lazy(() => import('@/pages/MarketplacePage'))
// Gap closures
const BackupPage = lazy(() => import('@/pages/BackupPage'))
const ChangelogPage = lazy(() => import('@/pages/ChangelogPage'))
const StatusPage = lazy(() => import('@/pages/StatusPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// NewBuildPage (/new) deleted 2026-05-26 — was a stale standalone path
// that duplicated the canonical /build + CreateApplicationDialog flow.
// All entry points to a new build now go through that dialog.
// SubscriptionPageV2 / CreditsPageV2 / InvoicesPageV2 deleted 2026-05-21 —
// their content was merged into /billing as deep-linkable tabs
// (?tab=plans, ?tab=buy, ?tab=invoices) — single page, single nav entry.
const OpsAuditLogPage = lazy(() => import('@/pages/ops/AuditLogPage'))
const OpsImpersonationPage = lazy(() => import('@/pages/ops/ImpersonationPage'))
const OpsMaintenancePage = lazy(() => import('@/pages/ops/MaintenanceWindowsPage'))


function PageLoader() {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <LoadingSpinner size="lg" />
    </div>
  )
}

/** Wraps a lazy page in ProtectedRoute + AppLayout + ErrorBoundary + Suspense */
function ProtectedPage({ page: Page, name, skipOnboardingCheck, allowPlatformAdmin, allowAnonymous }: {
  page: React.LazyExoticComponent<() => React.ReactElement> | React.LazyExoticComponent<React.ComponentType<Record<string, unknown>>>
  name: string
  skipOnboardingCheck?: boolean
  allowPlatformAdmin?: boolean
  allowAnonymous?: boolean
}) {
  return (
    <LaunchGate>
    <ProtectedRoute skipOnboardingCheck={skipOnboardingCheck} allowPlatformAdmin={allowPlatformAdmin} allowAnonymous={allowAnonymous}>
      <AppLayout>
        <PageErrorBoundary pageName={name}>
          <Suspense fallback={<PageSkeleton />}>
            <Page />
          </Suspense>
        </PageErrorBoundary>
      </AppLayout>
    </ProtectedRoute>
    </LaunchGate>
  )
}

/** Wraps a lazy page in ProtectedAdminRoute + AppLayout + ErrorBoundary + Suspense */
function AdminPage({ page: Page, name }: {
  page: React.LazyExoticComponent<() => React.ReactElement> | React.LazyExoticComponent<React.ComponentType<Record<string, unknown>>>
  name: string
}) {
  return (
    <LaunchGate>
    <ProtectedAdminRoute>
      <AppLayout>
        <PageErrorBoundary pageName={name}>
          <Suspense fallback={<PageSkeleton />}>
            <Page />
          </Suspense>
        </PageErrorBoundary>
      </AppLayout>
    </ProtectedAdminRoute>
    </LaunchGate>
  )
}

/** Redirect helper that forwards route params into the target path. */
function ParamRedirect({ to }: { to: string }) {
  const params = useParams()
  const resolved = Object.entries(params).reduce(
    (path, [key, value]) => path.replace(`:${key}`, value ?? ''),
    to,
  )
  return <Navigate to={resolved} replace />
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider>
              {/* RR-5 — keep the launch-gating module snapshot in sync with the
                  user's tier + backend flag overrides (renders nothing). */}
              <LaunchEntitlementsSync />
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    {/* Public legal pages — reachable signed-out (LandingPage footer). */}
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    {/* Public Help Center — landing + nested article slugs (docs/api/index). */}
                    <Route path="/docs" element={<DocsPage />} />
                    <Route path="/docs/*" element={<DocsPage />} />
                    {/* Public company pages (footer links). Changelog is public
                        info — no longer auth-gated. */}
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPage />} />
                    <Route path="/changelog" element={<ChangelogPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    {/* Public on purpose: the invitee is usually a new user with
                        no account yet. The page itself hands them to /login with
                        ?redirect back to this URL, so the token survives sign-up. */}
                    <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
                    <Route path="/onboarding" element={<Navigate to="/onboarding/setup" replace />} />
                    <Route
                      path="/onboarding/setup"
                      element={
                        <ProtectedRoute skipOnboardingCheck allowPlatformAdmin>
                          {/* Standalone full-screen flow — NO AppLayout shell.
                              The wizard has its own header+stepper; the dashboard
                              sidebar/topbar would be redundant double-chrome. */}
                          <Suspense fallback={<PageLoader />}>
                            <OnboardingSetupPage />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    {/* Plan 30 Lane 1 — /build is anonymous-friendly so
                        the fast-throwaway flow works without forcing
                        sign-up. BuildPage.handleSend branches on auth. */}
                    <Route path="/build" element={<ProtectedPage page={BuildPage} name="Build" skipOnboardingCheck allowAnonymous />} />
                    <Route path="/build/:projectId" element={<ProtectedPage page={BuildPage} name="Build" skipOnboardingCheck />} />
                    <Route path="/build/:projectId/:applicationId" element={<ProtectedPage page={BuildPage} name="Build" skipOnboardingCheck />} />
                    <Route path="/build/:projectId/:applicationId/:buildId" element={<ProtectedPage page={BuildPage} name="Build" skipOnboardingCheck />} />
                    <Route path="/templates" element={
                      <Suspense fallback={<PageLoader />}>
                        <TemplateGalleryPage />
                      </Suspense>
                    } />
                    <Route path="/templates/:slug" element={
                      <Suspense fallback={<PageLoader />}>
                        <TemplatePreviewPage />
                      </Suspense>
                    } />
                    <Route
                      path="/templates/:slug/open"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TemplateOpenPage />
                        </Suspense>
                      }
                    />
                    <Route path="/dashboard" element={<ProtectedPage page={DashboardPage} name="Dashboard" />} />
                    <Route path="/projects" element={<ProtectedPage page={ProjectsPage} name="Projects" />} />
                    <Route path="/settings" element={<ProtectedPage page={SettingsPage} name="Settings" allowPlatformAdmin />} />
                    <Route path="/rules" element={<ProtectedPage page={RulesPage} name="Rules" />} />
                    <Route path="/token-usage" element={<ProtectedPage page={TokenUsagePage} name="Token Usage" />} />
                    <Route path="/audit-log" element={<ProtectedPage page={AuditLogPage} name="Audit Log" allowPlatformAdmin />} />
                    <Route path="/knowledge" element={<ProtectedPage page={KnowledgePage} name="Knowledge" />} />
                    <Route path="/marketing" element={<ProtectedPage page={MarketingDashboardPage} name="Marketing" />} />
                    <Route path="/marketing/campaigns" element={<ProtectedPage page={CampaignManagerPage} name="Campaigns" />} />
                    <Route path="/marketing/calendar" element={<ProtectedPage page={ContentCalendarPage} name="Calendar" />} />
                    <Route path="/marketing/leads" element={<ProtectedPage page={LeadPipelinePage} name="Leads" />} />
                    <Route path="/billing" element={<ProtectedPage page={BillingPage} name="Billing" />} />
                    <Route path="/agency" element={<ProtectedPage page={AgencyOverviewPage} name="Agency" />} />
                    <Route path="/team" element={<ProtectedPage page={TeamPage} name="Team" />} />
                    <Route path="/notifications" element={<ProtectedPage page={NotificationsPage} name="Notifications" />} />
                    <Route path="/projects/:projectId/settings" element={<ProtectedPage page={ProjectSettingsPage} name="Project Settings" />} />
                    <Route path="/connectors" element={<ProtectedPage page={ConnectorSetupPage} name="Connectors" />} />
                    <Route path="/settings/connectors" element={<Navigate to="/connectors" replace />} />
                    <Route path="/marketing/ab-tests" element={<ProtectedPage page={ABTestPage} name="A/B Tests" />} />
                    <Route path="/marketing/analytics" element={<ProtectedPage page={CampaignAnalyticsPage} name="Analytics" />} />
                    <Route path="/marketing/campaigns/:id/analytics" element={<ProtectedPage page={CampaignAnalyticsPage} name="Campaign Analytics" />} />
                    <Route path="/marketing/nurture" element={<ProtectedPage page={NurtureEditorPage} name="Nurture Editor" />} />
                    <Route path="/marketing/personas" element={<ProtectedPage page={MarketingPersonasPage} name="Personas" />} />
                    <Route path="/marketing/strategy" element={<ProtectedPage page={MarketingStrategyPage} name="Strategy" />} />
                    <Route path="/marketing/nurture/:id/edit" element={<ProtectedPage page={NurtureEditorPage} name="Nurture Editor" />} />
                    <Route path="/model-settings" element={<ProtectedPage page={ModelSettingsPage} name="Model Settings" />} />
                    <Route path="/admin/providers" element={<AdminPage page={AdminProvidersPage} name="Admin Providers" />} />
                    <Route path="/admin/catalog" element={<AdminPage page={AdminCatalogPage} name="Admin Catalog" />} />
                    <Route path="/admin/agents" element={<AdminPage page={AdminAgentsPage} name="Admin Agents" />} />
                    <Route path="/admin/ops" element={<AdminPage page={AdminOpsPage} name="Admin Ops" />} />
                    <Route path="/admin/system" element={<AdminPage page={AdminSystemPage} name="Admin System" />} />
                    <Route path="/admin/support" element={<AdminPage page={AdminUserSupportPage} name="User Support" />} />
                    <Route path="/admin/promotions" element={<AdminPage page={AdminPromotionQueuePage} name="Promotion Queue" />} />
                    <Route path="/deployments" element={<ProtectedPage page={DeploymentPage} name="Deployments" />} />
                    <Route path="/deployments/:projectId" element={<ProtectedPage page={DeploymentPage} name="Deployments" />} />
                    <Route path="/traces" element={<ProtectedPage page={TracesPage} name="Traces" allowPlatformAdmin />} />
                    <Route path="/monitoring" element={<ProtectedPage page={MonitoringPage} name="Monitoring" allowPlatformAdmin />} />
                    <Route path="/brain" element={<ProtectedPage page={BrainPage} name="Brain" />} />
                    {/* /memory was the legacy tiered-memory inspector; the Brain page replaces it. */}
                    <Route path="/memory" element={<Navigate to="/brain" replace />} />
                    <Route path="/approvals" element={<ProtectedPage page={ApprovalQueuePage} name="Approvals" />} />
                    <Route path="/costs" element={<ProtectedPage page={CostForecastPage} name="Cost Forecast" />} />
                    <Route path="/apps/:appId/previews" element={<ProtectedPage page={AppPreviewsPage} name="App Previews" />} />
                    <Route path="/apps/:appId/logs" element={<ProtectedPage page={AppLogsPage} name="App Logs" />} />
                    <Route path="/apps/:appId/metrics" element={<ProtectedPage page={AppMetricsPage} name="App Metrics" />} />
                    <Route path="/help" element={<ProtectedPage page={HelpPage} name="Help" />} />
                    <Route path="/marketing/scheduler" element={<ProtectedPage page={SchedulerPage} name="Scheduler" />} />
                    <Route path="/marketing/geography" element={<ProtectedPage page={GeographyConfigPage} name="Geography" />} />
                    <Route path="/marketing/assets" element={<ProtectedPage page={AssetLibraryPage} name="Assets" />} />
                    <Route path="/marketing/tasks" element={<ProtectedPage page={TaskMonitorPage} name="Tasks" />} />
                    <Route path="/marketing/tracking" element={<ProtectedPage page={ConversionTrackingPage} name="Tracking" />} />
                    <Route path="/marketing/supply-demand" element={<ProtectedPage page={SupplyDemandPage} name="Supply Demand" />} />
                    <Route path="/marketing/seo" element={<ProtectedPage page={SeoManagerPage} name="SEO" />} />
                    <Route path="/marketing/webhooks" element={<ProtectedPage page={WebhookConfigPage} name="Webhooks" />} />
                    <Route path="/marketing/reports" element={<ProtectedPage page={ReportsPage} name="Reports" />} />
                    <Route path="/marketing/config" element={<ProtectedPage page={TenantConfigPage} name="Config" />} />
                    <Route path="/marketing/referrals" element={<ProtectedPage page={ReferralProgramPage} name="Referrals" />} />
                    <Route path="/marketing/providers" element={<ProtectedPage page={ProviderDiscoveryPage} name="Providers" />} />
                    <Route path="/content-studio" element={<ProtectedPage page={ContentStudioPage} name="Content Studio" />} />
                    <Route path="/content-studio/sessions/:sessionId" element={<ProtectedPage page={SessionDetailPage} name="Session Detail" />} />
                    <Route path="/content-studio/calendar" element={<ProtectedPage page={CalendarPage} name="Calendar" />} />
                    <Route path="/content-studio/ads" element={<ProtectedPage page={AdCreativesPage} name="Ad Creatives" />} />
                    <Route path="/content-studio/analytics" element={<ProtectedPage page={ContentAnalyticsPage} name="Analytics & Intelligence" />} />
                    <Route path="/content-studio/brand-voice/languages" element={<ProtectedPage page={LanguageVoicePage} name="Language Voice" />} />
                    <Route path="/content-studio/templates" element={<ProtectedPage page={TemplateMarketplacePage} name="Template Marketplace" />} />
                    <Route path="/content-studio/competitive" element={<ProtectedPage page={CompetitiveIntelPage} name="Competitive Intel" />} />
                    <Route path="/content-studio/cms" element={<ProtectedPage page={CmsIntegrationsPage} name="CMS Integrations" />} />
                    <Route path="/content-studio/bulk" element={<ProtectedPage page={BulkGenerationPage} name="Bulk Generation" />} />
                    <Route path="/content-studio/avatar" element={<ProtectedPage page={AvatarStudioPage} name="Avatar Studio" />} />
                    <Route path="/content-studio/video" element={<ProtectedPage page={VideoGenerationPage} name="Video Generation" />} />
                    <Route path="/content-studio/media" element={<ProtectedPage page={MediaStudioPage} name="Media Studio" />} />
                    <Route path="/content-studio/blog" element={<ProtectedPage page={BlogManagerPage} name="Blog Manager" />} />
                    {/* /build-analytics removed in Plan 30 A4 */}
                    <Route path="/developers" element={<ProtectedPage page={DeveloperPortalPage} name="Developer Portal" />} />
                    <Route path="/developers/earnings" element={<ProtectedPage page={AuthorEarningsPage} name="Author Earnings" />} />
                    <Route path="/compliance" element={<ProtectedPage page={ComplianceDashboardPage} name="Compliance" allowPlatformAdmin />} />
                    <Route path="/rbac-settings" element={<ProtectedPage page={RbacSettingsPage} name="RBAC Settings" allowPlatformAdmin />} />
                    <Route path="/sso-settings" element={<ProtectedPage page={SsoSettingsPage} name="SSO Settings" allowPlatformAdmin />} />
                    <Route path="/audit-log" element={<ProtectedPage page={AuditLogPage} name="Audit Log" allowPlatformAdmin />} />
                    <Route path="/sla" element={<ProtectedPage page={SlaDashboardPage} name="SLA Dashboard" allowPlatformAdmin />} />
                    {/* Phase 9: AI Agent Intelligence */}
                    <Route path="/knowledge-graph" element={<ProtectedPage page={KnowledgeGraphPage} name="Knowledge Graph" />} />
                    <Route path="/context-budget" element={<ProtectedPage page={ContextBudgetPage} name="Context Budget" />} />
                    {/* /agent-coordination + /error-prediction removed in Plan 30 A4 */}
                    <Route path="/self-healing" element={<ProtectedPage page={SelfHealingPage} name="Self-Healing" />} />
                    <Route path="/correction-learning" element={<ProtectedPage page={CorrectionLearningPage} name="Correction Learning" />} />
                    <Route path="/autonomous-dev" element={<ProtectedPage page={AutonomousDevPage} name="Autonomous Dev" />} />
                    {/* Phase 10: Growth & Distribution */}
                    <Route path="/shareable-previews" element={<ProtectedPage page={ShareablePreviewPage} name="Shareable Previews" />} />
                    <Route path="/referral-program" element={<ProtectedPage page={ReferralEnhancedPage} name="Referral Program" />} />
                    <Route path="/showcase" element={<ProtectedPage page={ShowcasePage} name="Showcase" />} />
                    <Route path="/widget-config" element={<ProtectedPage page={WidgetConfigPage} name="Widget Config" />} />
                    <Route path="/app-marketing" element={<ProtectedPage page={UserAppMarketingPage} name="App Marketing" />} />
                    {/* Plan 14: Company-in-a-Box */}
                    <Route path="/retention" element={<ProtectedPage page={RetentionDashboardPage} name="Retention" />} />
                    <Route path="/agency" element={<ProtectedPage page={AgencyDashboardPage} name="Agency" />} />
                    <Route path="/growth-setup" element={<ProtectedPage page={GrowthSetupPage} name="Growth Setup" />} />
                    <Route path="/onboarding-enhanced" element={<Navigate to="/onboarding/setup" replace />} />
                    {/* Outcome-based navigation entry points */}
                    <Route path="/create" element={<Navigate to="/content-studio" replace />} />
                    <Route path="/launch" element={<Navigate to="/marketing/campaigns" replace />} />
                    <Route path="/schedule" element={<Navigate to="/marketing/calendar" replace />} />
                    <Route path="/insights" element={<Navigate to="/marketing" replace />} />
                    <Route path="/assets" element={<Navigate to="/marketing/assets" replace />} />
                    <Route path="/intelligence" element={<Navigate to="/knowledge-graph" replace />} />
                    <Route path="/configure" element={<Navigate to="/token-usage" replace />} />
                    {/* Deep link redirects: /create */}
                    <Route path="/create/blog" element={<Navigate to="/content-studio/blog" replace />} />
                    <Route path="/create/video" element={<Navigate to="/content-studio/video" replace />} />
                    <Route path="/create/ads" element={<Navigate to="/content-studio/ads" replace />} />
                    <Route path="/create/bulk" element={<Navigate to="/content-studio/bulk" replace />} />
                    <Route path="/create/avatar" element={<Navigate to="/content-studio/avatar" replace />} />
                    <Route path="/create/brand-voice" element={<Navigate to="/content-studio/brand-voice/languages" replace />} />
                    <Route path="/create/templates" element={<Navigate to="/content-studio/templates" replace />} />
                    <Route path="/create/sessions/:id" element={<ParamRedirect to="/content-studio/sessions/:id" />} />
                    {/* Deep link redirects: /launch */}
                    <Route path="/launch/ab-tests" element={<Navigate to="/marketing/ab-tests" replace />} />
                    <Route path="/launch/nurture" element={<Navigate to="/marketing/nurture" replace />} />
                    <Route path="/launch/referrals" element={<Navigate to="/referral-program" replace />} />
                    <Route path="/launch/leads" element={<Navigate to="/marketing/leads" replace />} />
                    <Route path="/launch/seo-landing" element={<Navigate to="/marketing/seo" replace />} />
                    <Route path="/launch/app-marketing" element={<Navigate to="/app-marketing" replace />} />
                    <Route path="/launch/providers" element={<Navigate to="/marketing/providers" replace />} />
                    {/* Deep link redirects: /schedule */}
                    <Route path="/schedule/scheduler" element={<Navigate to="/marketing/scheduler" replace />} />
                    <Route path="/schedule/approvals" element={<Navigate to="/approvals" replace />} />
                    <Route path="/schedule/tasks" element={<Navigate to="/marketing/tasks" replace />} />
                    {/* Deep link redirects: /insights */}
                    <Route path="/insights/reports" element={<Navigate to="/marketing/reports" replace />} />
                    <Route path="/insights/competitive" element={<Navigate to="/content-studio/competitive" replace />} />
                    <Route path="/insights/seo" element={<Navigate to="/marketing/seo" replace />} />
                    <Route path="/insights/tracking" element={<Navigate to="/marketing/tracking" replace />} />
                    <Route path="/insights/supply-demand" element={<Navigate to="/marketing/supply-demand" replace />} />
                    <Route path="/insights/geography" element={<Navigate to="/marketing/geography" replace />} />
                    {/* Deep link redirects: /assets */}
                    <Route path="/assets/cms" element={<Navigate to="/content-studio/cms" replace />} />
                    <Route path="/assets/knowledge" element={<Navigate to="/knowledge" replace />} />
                    {/* Deep link redirects: /billing */}
                    <Route path="/billing/usage" element={<Navigate to="/token-usage" replace />} />
                    <Route path="/billing/forecast" element={<Navigate to="/costs" replace />} />
                    <Route path="/cost-metering" element={<ProtectedPage page={CostMeteringPage} name="Cost Metering" />} />
                    <Route path="/marketplace" element={<ProtectedPage page={MarketplacePage} name="Marketplace" />} />
                    <Route path="/backups" element={<ProtectedPage page={BackupPage} name="Backups" />} />
                    {/* /changelog is now a public route (see above) — no longer auth-gated. */}
                    <Route path="/status" element={<StatusPage />} />

                    {/* Build pipeline v2 routes */}
                    {/* /new deleted 2026-05-26 — replaced by /build hero +
                        CreateApplicationDialog (which is what users actually
                        hit from Dashboard → Create application AND
                        BuildPage → ApplicationSelector → Create application). */}
                    {/* /billing/v2/* routes deleted entirely 2026-05-21 — the
                        former content lives at /billing as deep-linkable tabs
                        (?tab=plans, ?tab=buy, ?tab=invoices). No nav link
                        ever pointed at /billing/v2/* so a bookmarked URL
                        falls through to the * NotFoundPage below. */}
                    <Route path="/ops/audit-log" element={<ProtectedPage page={OpsAuditLogPage} name="Audit log" allowPlatformAdmin />} />
                    <Route path="/ops/impersonation" element={<ProtectedPage page={OpsImpersonationPage} name="Impersonation" allowPlatformAdmin />} />
                    <Route path="/ops/maintenance" element={<ProtectedPage page={OpsMaintenancePage} name="Maintenance" allowPlatformAdmin />} />

                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  )
}
